// Product labels: the retail label on a product's bag or box, made by HHL Ops from the product itself so nobody types a
// SKU or UPC again. A label part printed as "HHL product label" (pack_print 'template') has a size (label_w_mm ×
// label_h_mm) and a line every product's label carries (label_note); each product whose BOM uses it gets its own label:
// the HHL logo, its name and variant, that line, where it's made, its SKU and its barcode (a UPC-A / EAN from Shopify,
// else a Code 128 of the SKU). The server draws them (server/src/product-labels.ts); the screens and the vendor's
// documents page link to them. This is the label generator HHL used before (handheldlegend.github.io), built in.
import { type BomIndex, type BomItem, bomOf, builtInside, resolve, variantsOf, whereUsed } from "./boms.ts";
import { code128 } from "./code128.ts";
import type { Item } from "./db.gen.ts";
import { type Gtin, gtinOf, gtinWidthModules } from "./ean.ts";
import { packagingOf } from "./projects.ts";

// Common label stock: shared/label-size.ts (every label size picker's module); kept importable from here too.
export { LABEL_SIZES } from "./label-size.ts";

import { LABEL_SIZES } from "./label-size.ts";
/** The old generator's default, 4 × 6 in shipping-label stock. */
export const DEFAULT_LABEL_SIZE = { w: 100, h: 150 };

/** The common stock a size is cut from, tall or wide; undefined for a size of its own. */
export const labelPreset = (s: { w: number; h: number }) => LABEL_SIZES.find((p) => (p.w === s.w && p.h === s.h) || (p.w === s.h && p.h === s.w));

/** A stock in the way a label is turned now: wide stays wide when another stock is picked. */
export const turnedLike = (stock: { w: number; h: number }, now: { w: number; h: number } | null) =>
  now && now.w !== now.h && stock.w !== stock.h && now.w > now.h !== stock.w > stock.h ? { w: stock.h, h: stock.w } : { w: stock.w, h: stock.h };

type LabelPart = Pick<Item, "part_type" | "pack_type" | "pack_print"> & Partial<Pick<Item, "label_w_mm" | "label_h_mm" | "label_note">>;

/** A part HHL Ops makes product labels for: a label part, or a bag or box that carries one (it's the same part). */
export const isProductLabel = (item: Partial<LabelPart> | undefined | null): boolean => !!item && packagingOf(item)?.print === "template";

/** A product label that's only a label (a label or sticker part), not a bag or box carrying one. */
export const isLabelOnly = (item: Partial<LabelPart> | undefined | null): boolean => {
  const pack = item ? packagingOf(item) : null;
  return pack?.print === "template" && (pack.type === "label" || pack.type === "sticker" || !pack.type);
};

/** Its size in mm, or null while it isn't set. */
export function labelSize(item: Partial<LabelPart>): { w: number; h: number } | null {
  const w = Number(item.label_w_mm);
  const h = Number(item.label_h_mm);
  return w > 0 && h > 0 ? { w, h } : null;
}
export const sizeText = (s: { w: number; h: number }) => `${s.w} × ${s.h} mm`;

/** The product labels a product (or any built item) carries: the HHL product label parts its current BOM puts in it. */
export function productLabelsFor(ix: BomIndex, item: BomItem): BomItem[] {
  const bom = bomOf(ix, item);
  if (!bom) return [];
  const out = new Map<string, BomItem>();
  for (const l of resolve(ix, bom, bom.shopify_product_id ? item : undefined).lines) if (isProductLabel(l.item)) out.set(l.item.id, l.item);
  return [...out.values()];
}

/**
 * Every product (variant, or built item) a label part labels, through kits built inside them too: where its BOMs put
 * it. A kit built in the same order as what uses it is never labelled (never on a shelf on its own: builtInside()).
 */
export function productsLabelled(ix: BomIndex, labelId: string, opts: { released?: boolean } = {}): BomItem[] {
  const out = new Map<string, BomItem>();
  // `released`: only products whose BOM in use is released, for what a vendor sees (a product still in its draft BOM
  // isn't announced yet).
  const counts = (v: BomItem) => !opts.released || bomOf(ix, v)?.status === "active";
  const visit = (usages: ReturnType<typeof whereUsed>) => {
    for (const u of usages) {
      const inside = u.bom.phantom || (u.parents.length > 0 && u.parents.every((p) => builtInside(u.bom, p.bom.make_vendor_id, "hhl_consigns")));
      if (!inside)
        for (const v of variantsOf(ix, u.bom)) if (!out.has(v.id) && counts(v) && productLabelsFor(ix, v).some((l) => l.id === labelId)) out.set(v.id, v);
      visit(u.parents);
    }
  };
  visit(whereUsed(ix, labelId));
  return [...out.values()].sort((a, b) => a.sku.localeCompare(b.sku, undefined, { numeric: true }));
}

export type LabelProduct = Pick<Item, "id" | "sku" | "name" | "barcode" | "country_of_origin" | "shopify_product_title" | "shopify_variant_title">;
export type LabelBarcode = { kind: "gtin"; gtin: Gtin } | { kind: "code128"; text: string };
export type LabelContent = {
  /** The product's name (Shopify's product title, else the item's name). */
  title: string;
  /** The variant ("Wireless / Atomic Purple"), '' for a product without variants. */
  subtitle: string;
  note: string;
  /** "Made in China", from the product's country of origin; '' when that isn't set. */
  origin: string;
  sku: string;
  barcode: LabelBarcode | null;
  /** What's off about it, for the screens ("No UPC in Shopify: the barcode is the SKU"). */
  warnings: string[];
};

const country = (code: string) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
};

/** The narrowest a bar may be and still scan: GS1's 80% of a UPC's 0.33 mm; Code 128 a little less. */
export const MIN_MODULE_MM = { gtin: 0.264, code128: 0.25 };

/**
 * About how wide one bar module comes out on a label this wide (its width less 5% each side, quiet zones included), so
 * the screens can warn before a vendor prints barcodes that don't scan.
 */
export function moduleMm(barcode: LabelBarcode, widthMm: number): number {
  const modules = barcode.kind === "gtin" ? gtinWidthModules(barcode.gtin) : code128(barcode.text).reduce((n, w) => n + w, 0) + 20;
  return (widthMm * 0.9) / modules;
}

/** Code 128 prints printable ASCII: dashes become "-", anything else is left out (and said). */
function code128Text(text: string, warnings: string[]): string {
  const clean = text.replace(/[‐-―−]/g, "-");
  const kept = clean.replace(/[^ -~]/g, "");
  if (kept !== clean) warnings.push(`“${text}” has characters a barcode can't hold: printed as “${kept}”`);
  return kept;
}

/** What a product's label says, from the product and the label part (its size too, for the barcode's width). */
export function labelContent(product: LabelProduct, label: Partial<Pick<Item, "label_note" | "label_w_mm" | "label_h_mm">>): LabelContent {
  const warnings: string[] = [];
  const variant = (product.shopify_variant_title ?? "").trim();
  const title = (product.shopify_product_title || product.name).trim();
  let barcode: LabelBarcode | null = null;
  const raw = product.barcode.trim();
  if (raw) {
    const g = gtinOf(raw);
    if ("error" in g) {
      warnings.push(`Its barcode ${raw} isn't a UPC or EAN (${g.error}): printed as Code 128`);
      barcode = { kind: "code128", text: code128Text(raw, warnings) };
    } else barcode = { kind: "gtin", gtin: g };
  } else if (product.sku) {
    warnings.push("No UPC in Shopify: the barcode is the SKU");
    barcode = { kind: "code128", text: code128Text(product.sku, warnings) };
  } else warnings.push("No UPC or SKU: no barcode");
  if (barcode?.kind === "code128" && !barcode.text) barcode = null;
  // Too narrow to scan on this label: say so before a vendor prints thousands.
  const size = labelSize(label);
  if (barcode && size) {
    const mm = moduleMm(barcode, size.w);
    if (mm < MIN_MODULE_MM[barcode.kind])
      warnings.push(`Its barcode is too narrow to scan on ${sizeText(size)} (${mm.toFixed(2)} mm bars): use wider label stock or a shorter SKU`);
  }
  if (!product.country_of_origin) warnings.push("No country of origin: no “Made in” line");
  return {
    title,
    subtitle: variant && variant !== "Default Title" ? variant : "",
    note: (label.label_note ?? "").trim(),
    origin: product.country_of_origin ? `Made in ${country(product.country_of_origin)}` : "",
    sku: product.sku,
    barcode,
    warnings,
  };
}
